export type DateFormatPreference = 'locale' | 'iso' | 'dmy' | 'dmy-short' | 'mdy' | 'short-month';
export type TimeFormatPreference = '24h' | '12h';

export type DatetimePreferences = {
  dateFormat: DateFormatPreference;
  timeFormat: TimeFormatPreference;
};

export const DATETIME_PREFERENCES_EVENT = 'datetime-preferences-changed';

const DATE_FORMAT_STORAGE_KEY = 'ics-ui:date-format';
const TIME_FORMAT_STORAGE_KEY = 'ics-ui:time-format';

const DEFAULT_PREFERENCES: DatetimePreferences = {
  dateFormat: 'locale',
  timeFormat: '24h',
};
let inMemoryPreferences: DatetimePreferences = DEFAULT_PREFERENCES;

const validDateFormats: DateFormatPreference[] = [
  'locale',
  'iso',
  'dmy',
  'dmy-short',
  'mdy',
  'short-month',
];
const validTimeFormats: TimeFormatPreference[] = ['24h', '12h'];

function readStorage(key: string): string | undefined {
  if (typeof window === 'undefined') {
    return undefined;
  }
  try {
    return window.localStorage.getItem(key) ?? undefined;
  } catch {
    return undefined;
  }
}

function writeStorage(key: string, value: string) {
  if (typeof window === 'undefined') {
    return;
  }
  try {
    window.localStorage.setItem(key, value);
  } catch {
    // Some browsers/privacy modes can block storage access.
  }
}

function isDateFormatPreference(value: string | undefined): value is DateFormatPreference {
  return !!value && validDateFormats.includes(value as DateFormatPreference);
}

function isTimeFormatPreference(value: string | undefined): value is TimeFormatPreference {
  return !!value && validTimeFormats.includes(value as TimeFormatPreference);
}

export function getDatetimePreferences(): DatetimePreferences {
  const dateFormatStorage = readStorage(DATE_FORMAT_STORAGE_KEY);
  const timeFormatStorage = readStorage(TIME_FORMAT_STORAGE_KEY);
  const preferences = {
    dateFormat: isDateFormatPreference(dateFormatStorage)
      ? dateFormatStorage
      : inMemoryPreferences.dateFormat,
    timeFormat: isTimeFormatPreference(timeFormatStorage)
      ? timeFormatStorage
      : inMemoryPreferences.timeFormat,
  };
  inMemoryPreferences = preferences;
  return preferences;
}

export function updateDatetimePreferences(next: Partial<DatetimePreferences>): DatetimePreferences {
  const current = getDatetimePreferences();
  const updated = { ...current, ...next };
  inMemoryPreferences = updated;

  writeStorage(DATE_FORMAT_STORAGE_KEY, updated.dateFormat);
  writeStorage(TIME_FORMAT_STORAGE_KEY, updated.timeFormat);

  if (typeof window !== 'undefined') {
    window.dispatchEvent(new CustomEvent(DATETIME_PREFERENCES_EVENT));
  }

  return updated;
}
